import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { PageHeader } from "../../component";
import ReusableField from "../../component/fields";
import FileUploader from "../../common/FileUploader";
import { useApi, useDirect } from "../../hooks";
import { apiPrArticle, method } from "../../apiPath";
import keyNames from "../../keyName";
import { getToast } from "../../common/utils";

const { get, post, put } = method;

const AddPrArticle = () => {
  const hitApi = useApi();
  const navigate = useNavigate();
  const { state } = useLocation();
  const directDispatch = useDirect();

  const storeData = useSelector((s) => s.mainReducer);
  const { prArticleData, extra, isLoading } = storeData;

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    setError,
    clearErrors,
  } = useForm();

  // Local states
  const [metaImage, setMetaImage] = useState(state?.metaImage || "");
  const [thumbnail, setThumbnail] = useState(state?.pr_thumbnail || "");
  const [isActive, setIsActive] = useState(
    state?.isActive !== undefined ? state.isActive : true
  );

  const [endPoint, setEndPoint] = useState({
    path: apiPrArticle,
    method: post,
    type: state === "add" ? "prepend" : "edit",
  });

  useEffect(() => {
    if (state && state !== "add") {
      setEndPoint({
        path: `${apiPrArticle}/${state._id}`,
        method: put,
        type: "edit",
      });

      // Prefill values
      setValue("metaTitle", state.metaTitle || "");
      setValue("metaDescription", state.metaDescription || "");
      setValue("pr_heading", state.pr_heading || "");
      setValue("pr_subheading", state.pr_subheading || "");
      setValue("pr_url", state.pr_url || "");
      setMetaImage(state.metaImage || "");
      setThumbnail(state.pr_thumbnail || "");
      setIsActive(state.isActive !== undefined ? state.isActive : true);
    }
  }, [state, setValue]);

  useEffect(() => {
    if (extra?.isNavigate === "pr-articles") {
      navigate("/app/pr-articles");
    }
    return () => directDispatch(null, keyNames.extra);
  }, [extra, directDispatch, navigate]);

  const allowedImageExts = [".png", ".jpg", ".jpeg", ".webp"];
  const isValidImageUrl = (url) => {
    const lower = (url || "").toLowerCase();
    return allowedImageExts.some((ext) => lower.endsWith(ext));
  };

  const handleMetaImageInvalid = (msg) => {
    setError("metaImage", { type: "manual", message: msg });
  };
  const handleThumbnailInvalid = (msg) => {
    setError("pr_thumbnail", { type: "manual", message: msg });
  };

  const onSubmit = (values) => {
    console.log("Form Values: ", values);

    if (metaImage && !isValidImageUrl(metaImage)) {
      handleMetaImageInvalid("Invalid image format");
      return;
    }
    if (thumbnail && !isValidImageUrl(thumbnail)) {
      handleThumbnailInvalid("Invalid thumbnail format");
      return;
    }

    const finalData = {
      ...values,
      metaImage,
      pr_thumbnail: thumbnail,
      isActive,
    };

    hitApi(
      endPoint.path,
      finalData,
      endPoint.method,
      keyNames.prArticleData,
      getToast("success"),
      { type: endPoint.type, isNavigate: "pr-articles" }
    );
  };

  const handleMetaImageUpload = (url) => {
    setMetaImage(url);
    setValue("metaImage", url, { shouldValidate: true }); // <— add this
    clearErrors("metaImage");
  };

  const handleThumbnailUpload = (url) => {
    setThumbnail(url);
    setValue("pr_thumbnail", url, { shouldValidate: true }); // <— add this
    clearErrors("pr_thumbnail");
  };

  const btn = {
    back: () => navigate("/app/pr-articles"),
    show: false,
    text: "Save & Publish",
    type: "contained",
  };

  return (
    <>
      <PageHeader
        title={state?._id ? "Edit PR Article" : "Add New PR Article"}
        btn={btn}
      />
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{ mt: 3 }}
      >
        {/* Meta Section */}
        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Meta Section" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 12 }}>
                <Typography sx={{ mb: 1 }}>Meta Title</Typography>
                <ReusableField
                  label="Meta Title"
                  name="metaTitle"
                  type="text"
                  placeholder="Enter Meta Title"
                  fullWidth={true}
                  {...register("metaTitle")}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Card elevation={1} sx={{ p: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <FileUploader
                      url={metaImage}
                      urlType="image"
                      type="metaImage"
                      requiredWidth={1200}
                      requiredHeight={630}
                      onFileUpload={handleMetaImageUpload}
                      onInvalidFile={(msg) => handleMetaImageInvalid(msg)}
                      showPreview={false}
                      buttonText="Upload Image"
                    />
                    <ReusableField
                      label="Meta Image"
                      name="metaImage"
                      type="text"
                      value={metaImage}
                      onChange={(e) => setMetaImage(e.target.value)}
                      placeholder="Meta Image URL"
                      fullWidth={true}
                      error={!!formErrors.metaImage}
                      helperText={
                        formErrors.metaImage?.message || (
                          <span>
                            <span style={{ color: "#d32f2f" }}>*</span> Meta
                            Image size: (1200 x 630) px
                          </span>
                        )
                      }
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item size={{ md: 6, xs: 12 }}>
                <Typography sx={{ mb: 1 }}>Meta Description</Typography>
                <ReusableField
                  label="Meta Description"
                  name="metaDescription"
                  type="textarea"
                  placeholder="Enter Meta Description"
                  rows={4}
                  multiline={true}
                  fullWidth={true}
                  {...register("metaDescription")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* PR Details */}
        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="PR Details" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <ReusableField
                  label="PR Heading"
                  name="pr_heading"
                  type="text"
                  placeholder="Enter PR Heading"
                  fullWidth
                  error={!!formErrors.pr_heading}
                  helperText={formErrors.pr_heading?.message}
                  {...register("pr_heading", {
                    required: "PR Heading is required",
                  })}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <ReusableField
                  label="PR Subheading"
                  name="pr_subheading"
                  type="text"
                  placeholder="Enter PR Subheading"
                  fullWidth
                  {...register("pr_subheading")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <ReusableField
                  label="PR URL"
                  name="pr_url"
                  type="url"
                  placeholder="https://example.com"
                  fullWidth
                  {...register("pr_url")}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }}>
                <Card elevation={2} sx={{ px: 3, py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <ReusableField
                      type="switch"
                      label="Is Active"
                      name="isActive"
                      value={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  </Box>
                </Card>
              </Grid>
              <Grid item size={{ xs: 12, md: 8 }}>
                <Card elevation={1} sx={{ p: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <Typography>Thumbnail</Typography>
                    <FileUploader
                      url={thumbnail}
                      type="pr_thumbnail"
                      urlType="image"
                      requiredWidth={300}
                      requiredHeight={200}
                      onFileUpload={handleThumbnailUpload}
                      onInvalidFile={(msg) => handleThumbnailInvalid(msg)}
                      showPreview={false}
                      buttonText="Upload Image"
                    />
                    <ReusableField
                      label="Thumbnail URL"
                      name="pr_thumbnail"
                      type="text"
                      value={thumbnail}
                      onChange={(e) => setThumbnail(e.target.value)}
                      placeholder="Thumbnail URL"
                      fullWidth={true}
                      error={!!formErrors.pr_thumbnail}
                      helperText={
                        formErrors.pr_thumbnail?.message || (
                          <span>
                            <span style={{ color: "#d32f2f" }}>*</span>{" "}
                            Thumbnail size: (300 x 200) px
                          </span>
                        )
                      }
                    />
                  </Box>
                </Card>
              </Grid>
              {/* <Grid item size={{ xs: 12, md: 6 }}>
                <FileUploader
                  url={thumbnail}
                  urlType="image"
                  type="pr_thumbnail"
                  requiredWidth={300}
                  requiredHeight={200}
                  onFileUpload={handleThumbnailUpload}
                  onInvalidFile={(msg) => handleThumbnailInvalid(msg)}
                  buttonText="Upload Thumbnail"
                />
                <ReusableField
                  label="Thumbnail URL"
                  name="pr_thumbnail"
                  type="text"
                  value={thumbnail}
                  onChange={(e) => setThumbnail(e.target.value)}
                  fullWidth
                  error={!!formErrors.pr_thumbnail}
                  helperText={formErrors.pr_thumbnail?.message}
                />
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            size="large"
            type="submit"
            variant="contained"
            sx={{ px: 5, py: 1.5, fontSize: "1rem", borderRadius: "8px" }}
            loading={isLoading === "prArticleData"}
            loadingPosition="start"
          >
            Save & Publish
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AddPrArticle;
